/**
 * Staff Console -> SEO tab.
 *
 * The daily crawl (seo/monitor.py) used to only commit markdown to the repo,
 * which meant checking it required opening GitHub Actions. Nobody keeps doing
 * that, so a monitor nobody reads is a monitor that does not exist. Runs now
 * land in D1 and surface here alongside Lifecycle and Orchestrator.
 *
 * Reading the page: zero high and zero medium is the healthy state. The
 * "since last run" line is the part worth looking at daily; the full issue
 * list is there for when something changes.
 */

import {
  Loader2, RefreshCw, Search, AlertTriangle, CheckCircle2, FileText,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import { useAdminSeo, type SeoIssue } from './use-admin'

function sevClass(s: SeoIssue['severity']): string {
  if (s === 'high') return 'bg-[rgba(248,113,113,0.12)] text-[#F87171]'
  if (s === 'medium') return 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]'
  return 'bg-[rgba(255,255,255,0.06)] text-[var(--color-silver)]'
}

export function SeoPage() {
  const { data, isLoading, refetch, isRefetching } = useAdminSeo()

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading SEO runs…
      </div>
    )
  }

  const run = data.latest
  if (!run) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-[var(--color-silver)]">
          No crawls recorded yet. The monitor runs daily at 07:20 UTC and posts
          its result here. You can also trigger it by hand from the SEO Monitor
          workflow in GitHub Actions.
        </CardContent>
      </Card>
    )
  }

  const healthy = run.high === 0 && run.medium === 0
  const stats: Array<{ label: string; value: number | string; tone: string }> = [
    { label: 'Pages crawled', value: run.pages_crawled, tone: 'text-[var(--color-silver-bright)]' },
    { label: 'High', value: run.high, tone: run.high > 0 ? 'text-[#F87171]' : 'text-[#34D399]' },
    { label: 'Medium', value: run.medium, tone: run.medium > 0 ? 'text-[#FBBF24]' : 'text-[#34D399]' },
    { label: 'Low', value: run.low, tone: 'text-[var(--color-silver)]' },
    { label: 'New today', value: run.new_count, tone: run.new_count > 0 ? 'text-[#FBBF24]' : 'text-[var(--color-silver)]' },
    { label: 'Fixed', value: run.fixed_count, tone: run.fixed_count > 0 ? 'text-[#34D399]' : 'text-[var(--color-silver)]' },
  ]

  const pageRows = Object.entries(run.pages)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-[var(--color-silver)]">
          <Search className="h-4 w-4 text-[#8FB4FA]" />
          Last crawl <span className="tabular-nums">{fmtLocale(run.ran_at)}</span>
          {healthy ? (
            <Badge variant="outline" className="border-none bg-[rgba(16,185,129,0.12)] text-xs text-[#34D399]">
              <CheckCircle2 className="mr-1 h-3 w-3" /> healthy
            </Badge>
          ) : (
            <Badge variant="outline" className="border-none bg-[rgba(248,113,113,0.12)] text-xs text-[#F87171]">
              <AlertTriangle className="mr-1 h-3 w-3" /> needs attention
            </Badge>
          )}
        </div>
        <button
          onClick={() => void refetch()}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)]"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {stats.map((s) => (
          <Card key={s.label} className="flex-1 min-w-[130px]">
            <CardContent className="p-4">
              <p className="eyebrow text-[var(--color-silver)]">{s.label}</p>
              <p className={cn('mt-1 text-2xl font-bold tabular-nums', s.tone)}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {run.targets_todo.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
              <FileText className="h-4 w-4 text-[#8FB4FA]" />
              Next pages to build
              <span className="text-xs font-normal text-[var(--color-silver)]">
                (highest buyer intent first)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {run.targets_todo.slice(0, 6).map((t) => (
              <div key={t.path} className="border-b border-[var(--color-border)] pb-2 last:border-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-[var(--color-silver-bright)]">{t.keyword}</span>
                  <code className="text-xs text-[var(--color-silver)]">{t.path}</code>
                  <Badge variant="outline" className="border-none bg-[rgba(255,255,255,0.06)] text-[10px] text-[var(--color-silver)]">
                    {t.intent}
                  </Badge>
                </div>
                {t.why && <p className="text-xs text-[var(--color-silver)]">{t.why}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">
            Open issues
            <span className="ml-2 text-xs font-normal text-[var(--color-silver)]">
              ({run.issues.length} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {run.issues.length === 0 ? (
            <p className="py-3 text-sm text-[var(--color-silver)]">
              Nothing outstanding. Every check passed on the last crawl.
            </p>
          ) : (
            <div className="space-y-1.5">
              {run.issues.map((i, n) => (
                <div key={`${i.page}-${n}`} className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge variant="outline" className={cn('border-none text-[10px] uppercase', sevClass(i.severity))}>
                    {i.severity}
                  </Badge>
                  <code className="text-xs text-[var(--color-silver-bright)]">{i.page}</code>
                  <span className="text-[var(--color-silver)]">{i.issue}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">Page inventory</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="text-right">Title</TableHead>
                <TableHead className="text-right">Desc</TableHead>
                <TableHead className="text-right">H1s</TableHead>
                <TableHead className="text-right">Words</TableHead>
                <TableHead className="text-right">JSON-LD</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map(([url, p]) => {
                let path = url
                try { path = new URL(url).pathname || '/' } catch { /* keep raw */ }
                return (
                  <TableRow key={url}>
                    <TableCell className="text-xs text-[var(--color-silver-bright)]">{path}</TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', p.title_len > 62 && 'text-[#FBBF24]')}>
                      {p.title_len}
                    </TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', (p.desc_len > 160 || p.desc_len < 70) && 'text-[#FBBF24]')}>
                      {p.desc_len}
                    </TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', p.h1_count !== 1 && 'text-[#FBBF24]')}>
                      {p.h1_count}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.words}</TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', p.jsonld_invalid > 0 && 'text-[#F87171]')}>
                      {p.jsonld_blocks}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.history.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[var(--color-silver-bright)]">
              History
              <span className="ml-2 text-xs font-normal text-[var(--color-silver)]">
                (last {data.history.length} runs)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run</TableHead>
                  <TableHead className="text-right">Pages</TableHead>
                  <TableHead className="text-right">High</TableHead>
                  <TableHead className="text-right">Medium</TableHead>
                  <TableHead className="text-right">Low</TableHead>
                  <TableHead className="text-right">New</TableHead>
                  <TableHead className="text-right">Fixed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...data.history].reverse().map((h) => (
                  <TableRow key={h.ran_at}>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">{fmtLocale(h.ran_at)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{h.pages_crawled}</TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', h.high > 0 && 'text-[#F87171]')}>{h.high}</TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', h.medium > 0 && 'text-[#FBBF24]')}>{h.medium}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{h.low}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{h.new_count}</TableCell>
                    <TableCell className={cn('text-right text-xs tabular-nums', h.fixed_count > 0 && 'text-[#34D399]')}>{h.fixed_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
